import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, imageUrl, formatRupiah, formatDiscount } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { getStatus, STATUS_BADGE_CLASSES } from "../lib/status";
import { Search, ArrowUpRight, Package, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import QRScannerDialog from "../components/QRScannerDialog";

export default function PublicCatalog() {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "");
    const [activeBrand, setActiveBrand] = useState(searchParams.get("brand") || "");
    const [scannerOpen, setScannerOpen] = useState(false);

    const handleScan = (text) => {
        setScannerOpen(false);
        if (!text) return;
        // Accept patterns: "/p/slug", "https://renopoint.net/p/slug", or raw slug.
        let slug = null;
        try {
            const u = new URL(text);
            const m = u.pathname.match(/\/p\/([^/?#]+)/);
            if (m) slug = m[1];
        } catch {
            const m = text.match(/\/p\/([^/?#]+)/) || text.match(/^[a-z0-9-]+$/i);
            if (m) slug = m[1] || m[0];
        }
        if (slug) {
            navigate(`/p/${slug}`);
        } else {
            toast.error("QR code tidak dikenali. Pastikan dipindai dari label produk Reno Point.");
        }
    };

    // Sync filter state → URL search params (so links from WhatsApp catalog PDF work)
    useEffect(() => {
        const newParams = {};
        if (q) newParams.q = q;
        if (activeCategory) newParams.category = activeCategory;
        if (activeBrand) newParams.brand = activeBrand;
        setSearchParams(newParams, { replace: true });
    }, [q, activeCategory, activeBrand, setSearchParams]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const params = {};
                if (q) params.q = q;
                if (activeCategory) params.category = activeCategory;
                if (activeBrand) params.brand = activeBrand;
                const { data } = await api.get("/products", { params });
                if (!cancelled) setProducts(data);
            } catch (error) {
                console.error("Failed to load products:", error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        const t = setTimeout(load, 200);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [q, activeCategory, activeBrand]);

    const categories = Array.from(
        new Set(products.map((p) => p.category).filter(Boolean))
    ).sort();

    const clearBrandFilter = () => setActiveBrand("");

    return (
        <div data-testid="public-catalog-page" className="min-h-screen">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border bg-[hsl(var(--muted))] grain">
                <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20 lg:py-24">
                    <div className="max-w-2xl fade-up">
                        <span className="label-eyebrow text-accent" data-testid="hero-eyebrow">
                            {settings.tagline}
                        </span>
                        <h1 className="mt-4 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl text-balance">
                            <span data-testid="hero-store-name">{settings.name}</span>
                            <br />
                            <span className="text-accent">tinggal pindai & lihat.</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                            Pelanggan memindai QR di toko untuk melihat detail, harga
                            dalam Rupiah, ukuran, jenis, dan deskripsi produk — tanpa instal aplikasi.
                        </p>
                        <div className="mt-7 flex flex-wrap items-center gap-3">
                            <button
                                onClick={() => setScannerOpen(true)}
                                data-testid="hero-scan-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:opacity-90 transition-all duration-200 shadow-lg shadow-accent/20"
                            >
                                <ScanLine size={16} strokeWidth={2} /> Pindai QR Produk
                            </button>
                            <a
                                href="#produk"
                                data-testid="hero-browse-cta"
                                className="inline-flex items-center gap-2 rounded-full border border-border bg-white/60 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-secondary transition-all duration-200"
                            >
                                Jelajahi Produk
                            </a>
                            {user && (
                                <Link
                                    to="/admin"
                                    data-testid="hero-admin-cta"
                                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white/60 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-secondary transition-all duration-200"
                                >
                                    Kelola Katalog <ArrowUpRight size={16} strokeWidth={1.75} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Search */}
            <section id="produk" className="mx-auto max-w-6xl px-5 py-10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <span className="label-eyebrow text-muted-foreground">Semua Produk</span>
                        <h2 className="mt-1 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
                            {products.length} produk tersedia
                        </h2>
                    </div>
                    <div className="relative w-full sm:max-w-xs">
                        <Search
                            size={16}
                            strokeWidth={1.75}
                            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                            data-testid="catalog-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cari produk, SKU…"
                            className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-primary"
                        />
                    </div>
                </div>

                {/* Active filter badges (brand) */}
                {activeBrand && (
                    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
                        <span className="text-xs text-muted-foreground">Filter aktif:</span>
                        <button
                            data-testid="brand-filter-badge"
                            onClick={clearBrandFilter}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 transition-opacity"
                        >
                            Brand: {activeBrand}
                            <X size={12} strokeWidth={2} />
                        </button>
                    </div>
                )}

                {/* Category chips */}
                {categories.length > 0 && (
                    <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-chips">
                        <CategoryChip
                            active={activeCategory === ""}
                            onClick={() => setActiveCategory("")}
                            label="Semua"
                        />
                        {categories.map((c) => (
                            <CategoryChip
                                key={c}
                                active={activeCategory === c}
                                onClick={() => setActiveCategory(c)}
                                label={c}
                            />
                        ))}
                    </div>
                )}

                {/* Grid */}
                <div className="mt-8">
                    {loading ? (
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={`skeleton-${i}`} className="aspect-square animate-pulse rounded-lg bg-secondary" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 stagger">
                            {products.map((p) => (
                                <ProductCard key={p.id} p={p} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <footer className="mt-16 border-t border-border bg-[hsl(var(--muted))]">
                <div className="mx-auto max-w-6xl px-5 py-8 text-sm text-muted-foreground flex flex-wrap gap-4 items-center justify-between">
                    <span>© {new Date().getFullYear()} {settings.name}</span>
                    <span className="text-xs uppercase tracking-[0.2em]">Web-based · Tanpa Instal</span>
                </div>
            </footer>
            <button
                onClick={() => setScannerOpen(true)}
                data-testid="floating-scan-btn"
                aria-label="Pindai QR"
                title="Pindai QR Produk"
                className="fixed bottom-5 left-5 z-50 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg shadow-primary/30 hover:scale-105 active:scale-95 transition-all sm:px-5 sm:py-3.5"
            >
                <ScanLine size={20} strokeWidth={2.1} />
                <span className="hidden sm:inline text-sm font-semibold">Pindai QR</span>
            </button>
            <QRScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onDecode={handleScan}
            />
        </div>
    );
}

function CategoryChip({ active, onClick, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`category-chip-${label}`}
            className={
                "shrink-0 rounded-full border px-4 py-1.5 text-sm transition-all duration-200 " +
                (active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-white text-foreground hover:bg-secondary")
            }
        >
            {label}
        </button>
    );
}

function ProductCard({ p }) {
    const hasDiscount = p.list_price && p.list_price > p.price && p.discount_percent > 0;
    const status = getStatus(p.status);
    const statusClass = STATUS_BADGE_CLASSES[status.tone];
    const unavailable = p.status === "habis" || p.status === "discontinued";
    return (
        <Link to={`/p/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block">
            <div className={`relative aspect-square overflow-hidden rounded-lg border border-border bg-[#FDFBF7] ${unavailable ? "opacity-60" : ""}`}>
                {p.image_path ? (
                    <img
                        src={imageUrl(p.image_path)}
                        alt={p.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <Package strokeWidth={1.25} size={40} />
                    </div>
                )}
                {hasDiscount && (
                    <span className="absolute left-2 top-2 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
                        -{formatDiscount(p.discount_percent)}%
                    </span>
                )}
                {p.status && p.status !== "ready" && (
                    <span
                        data-testid={`card-status-${p.slug}`}
                        className={`absolute right-2 top-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm ${statusClass}`}
                    >
                        {status.label}
                    </span>
                )}
            </div>
            <div className="mt-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="truncate font-heading text-base font-medium text-foreground">{p.name}</h3>
                    {p.category && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.category}</p>
                    )}
                </div>
                <div className="shrink-0 text-right">
                    {hasDiscount && (
                        <div className="text-[11px] text-muted-foreground line-through">{formatRupiah(p.list_price)}</div>
                    )}
                    <div className="font-heading text-sm font-semibold text-foreground">{formatRupiah(p.price)}</div>
                </div>
            </div>
        </Link>
    );
}

function EmptyState() {
    return (
        <div
            data-testid="catalog-empty-state"
            className="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border border-dashed border-border bg-white p-12 text-center"
        >
            <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-primary">
                <Package size={22} strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 font-heading text-lg font-semibold">Belum ada produk</h3>
            <p className="mt-1 text-sm text-muted-foreground">
                Login sebagai admin dan unggah Excel untuk mulai mengisi katalog.
            </p>
            <Link
                to="/admin/login"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
                Login Admin
            </Link>
        </div>
    );
}
